import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, User, Percent, Target, Plus, Trash } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { PERMISSION_LABELS, hasPerm, fmtCurrency } from "../lib/format";
import api, { apiErrorMessage } from "../lib/api";

function Info({ label, value, cap }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className={`mt-0.5 font-medium text-slate-800 ${cap ? "capitalize" : ""}`}>{value}</dd>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const canIncentive = hasPerm(user, "manage_incentive_settings");
  const canTargets = hasPerm(user, "manage_floor_targets");

  const [basePct, setBasePct] = useState("");
  const [targets, setTargets] = useState([]);
  const [tForm, setTForm] = useState({ floor_name: "", description: "", monthly_target: "", active: true });

  useEffect(() => {
    if (canIncentive || canTargets) {
      api.get(canIncentive ? "/settings/incentive" : "/settings/targets").then((r) => {
        if (canIncentive) { setBasePct(String(r.data.base_pct)); setTargets(r.data.floor_targets || []); }
        else setTargets(r.data || []);
      }).catch(() => {});
    }
  }, [canIncentive, canTargets]);

  const saveBasePct = async () => {
    try {
      const r = await api.put("/settings/incentive", { base_pct: parseFloat(basePct) });
      setBasePct(String(r.data.base_pct));
      toast.success("Incentive rate updated");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail) || "Failed"); }
  };

  const saveTarget = async () => {
    if (!tForm.floor_name || tForm.monthly_target === "") { toast.error("Floor name and target required"); return; }
    try {
      const r = await api.put("/settings/targets", { ...tForm, monthly_target: parseFloat(tForm.monthly_target) });
      setTargets(r.data);
      setTForm({ floor_name: "", description: "", monthly_target: "", active: true });
      toast.success("Target saved");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail) || "Failed"); }
  };

  const delTarget = async (name) => {
    try { const r = await api.delete(`/settings/targets/${encodeURIComponent(name)}`); setTargets(r.data); toast.success("Removed"); }
    catch (e) { toast.error("Failed"); }
  };

  const toggleActive = async (t) => {
    try { const r = await api.put("/settings/targets", { ...t, active: !t.active }); setTargets(r.data); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <Layout title="Settings">
      <div className="animate-fade-in max-w-3xl space-y-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2 font-display text-base font-bold text-slate-800"><User size={18} weight="bold" /> Account</div>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <Info label="Name" value={user?.name} />
            <Info label="Username" value={`@${user?.username}`} />
            <Info label="Role" value={user?.role} cap />
            <Info label="Salesman" value={user?.salesman_name || "—"} />
            <Info label="Current floor" value={user?.current_floor || "—"} />
            <Info label="Core floor" value={user?.core_floor || "—"} />
          </dl>
          <p className="mt-4 text-xs text-slate-400">To change your password, ask an administrator to reset it from the Users page.</p>
        </div>

        {canIncentive && (
          <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="incentive-settings">
            <div className="mb-4 flex items-center gap-2 font-display text-base font-bold text-slate-800"><Percent size={18} weight="bold" /> Incentive Rate</div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Base incentive %</label>
                <input data-testid="base-pct-input" type="number" step="0.01" value={basePct} onChange={(e) => setBasePct(e.target.value)}
                  className="w-40 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700" />
              </div>
              <button onClick={saveBasePct} data-testid="save-base-pct" className="rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800">Save</button>
            </div>
            <p className="mt-2 text-xs text-slate-400">Doubles automatically when a salesman's core floor achieves its monthly target. Completed months keep their historical rate.</p>
          </div>
        )}

        {canTargets && (
          <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="floor-target-settings">
            <div className="mb-4 flex items-center gap-2 font-display text-base font-bold text-slate-800"><Target size={18} weight="bold" /> Floor Targets</div>
            <div className="space-y-2">
              {targets.map((t) => (
                <div key={t.floor_name} data-testid={`target-${t.floor_name}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{t.floor_name} {!t.active && <span className="text-xs text-slate-400">(inactive)</span>}</div>
                    <div className="text-xs text-slate-500">{t.description || "—"} · Target {fmtCurrency(t.monthly_target)}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => toggleActive(t)} className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-white">{t.active ? "Deactivate" : "Activate"}</button>
                    <button onClick={() => delTarget(t.floor_name)} data-testid={`del-target-${t.floor_name}`} className="rounded-md border border-slate-300 p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash size={14} /></button>
                  </div>
                </div>
              ))}
              {targets.length === 0 && <p className="text-sm text-slate-500">No floor targets yet.</p>}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-2 rounded-md border border-dashed border-slate-300 p-3 sm:grid-cols-4">
              <input data-testid="target-floor-name" placeholder="Floor (exact Excel value)" value={tForm.floor_name} onChange={(e) => setTForm({ ...tForm, floor_name: e.target.value })} className="rounded-md border border-slate-300 px-2 py-2 text-sm" />
              <input data-testid="target-desc" placeholder="Description" value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} className="rounded-md border border-slate-300 px-2 py-2 text-sm" />
              <input data-testid="target-amount" type="number" placeholder="Monthly target ₹" value={tForm.monthly_target} onChange={(e) => setTForm({ ...tForm, monthly_target: e.target.value })} className="rounded-md border border-slate-300 px-2 py-2 text-sm" />
              <button onClick={saveTarget} data-testid="add-target-btn" className="flex items-center justify-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Plus size={14} weight="bold" /> Add / Update</button>
            </div>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2 font-display text-base font-bold text-slate-800"><ShieldCheck size={18} weight="bold" /> Your permissions</div>
          <div className="flex flex-wrap gap-2">
            {(user?.permissions || []).map((p) => (
              <span key={p} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{PERMISSION_LABELS[p] || p}</span>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
